import apiClient from "@services/api/apiClient.jsx";

const roleApi = {
    // 모든 역할 목록 가져오기
    getRoles: async () => {
        try {
            const response = await apiClient.get("/infra/roles");
            return response.data;
        } catch (error) {
            console.error("역할 목록 가져오기 실패:", error);
            throw error;
        }
    },
    
    // 특정 역할 타입으로 필터링된 역할 목록 가져오기
    getRolesByType: async (roleType) => {
        try {
            const response = await apiClient.get(`/infra/roles/type/${roleType}`);
            return response.data;
        } catch (error) {
            console.error(`${roleType} 역할 목록 가져오기 실패:`, error);
            throw error;
        }
    },
    
    // 페이징 처리된 역할 그룹 목록 가져오기
    // /infra/roles 엔드포인트를 사용하여 그룹처럼 처리
    getRoleGroups: async (page = 0, size = 10, sort = "createdAt,desc") => {
        try {
            const response = await apiClient.get("/infra/roles", {
                params: { page, size, sort }
            });

            // 백엔드에서 받은 Role 데이터를 Group 형식으로 변환
            if (response.data && response.data.data) {
                const roleData = response.data.data;

                // Role 데이터를 Group 형식으로 매핑
                if (roleData.content) {
                    roleData.content = roleData.content.map(role => ({
                        ...role,
                        permissions: role.permissions || [],
                        branch: role.requiresBranch === false ? '전체' : (role.branchName || '-'), // ADMIN은 '전체', 나머지는 브랜치명
                        roleType: role.roleType || "ROLE_USER", // 역할 타입 유지
                        requiresBranch: role.requiresBranch !== false // 지점 필요 여부
                    }));
                }

                return response.data;
            }

            return response.data;
        } catch (error) {
            console.error("역할 그룹 목록 가져오기 실패:", error);
            throw error;
        }
    },
    
    // 역할 그룹 추가 (실제로는 Role 생성)
    createRoleGroup: async (groupData) => {
        try {
            // Group 데이터를 Role API가 기대하는 형식으로 변환
            const roleData = {
                name: groupData.name || groupData.groupName,
                description: groupData.description || `${groupData.groupName} 그룹`,
                roleType: groupData.roleType || "ROLE_USER", // 역할 타입 (기본값: ROLE_USER)
                requiresBranch: groupData.roleType !== "ROLE_ADMIN", // ADMIN은 지점 불필요
                branchId: groupData.branchId || null, // branchId 추가
                permissionIds: groupData.permissionIds || []
            };

            const response = await apiClient.post("/infra/roles", roleData);

            // 응답을 Group 형식으로 변환
            if (response.data && response.data.data) {
                response.data.data.branch = groupData.branch || '-';
            }

            return response.data;
        } catch (error) {
            console.error("역할 그룹 추가 실패:", error);
            throw error;
        }
    },
    
    // 역할 그룹 수정 (실제로는 Role 수정)
    updateRoleGroup: async (groupId, groupData) => {
        try {
            // Group 데이터를 Role API가 기대하는 형식으로 변환
            const roleData = {
                name: groupData.name || groupData.groupName,
                description: groupData.description || `${groupData.groupName} 그룹`,
                roleType: groupData.roleType, // 역할 타입 유지
                requiresBranch: groupData.roleType !== "ROLE_ADMIN", // ADMIN은 지점 불필요
                branchId: groupData.branchId || null, // branchId 추가
                permissionIds: groupData.permissionIds || []
            };

            const response = await apiClient.put(`/infra/roles/${groupId}`, roleData);

            // 응답을 Group 형식으로 변환
            if (response.data && response.data.data) {
                response.data.data.branch = groupData.branch || '-';
            }

            return response.data;
        } catch (error) {
            console.error("역할 그룹 수정 실패:", error);
            throw error;
        }
    },
    
    // 역할 그룹 삭제 (실제로는 Role 삭제)
    deleteRoleGroup: async (groupId) => {
        try {
            const response = await apiClient.delete(`/infra/roles/${groupId}`);
            return response.data;
        } catch (error) {
            console.error("역할 그룹 삭제 실패:", error);
            throw error;
        }
    },
    
    // 역할 그룹명 중복 확인
    // 전체 목록에서 이름으로 검색하여 중복 확인
    checkGroupNameDuplicate: async (groupName) => {
        try {
            // 전체 Role 목록을 가져와서 이름 중복 확인
            const response = await apiClient.get("/infra/roles", {
                params: { name: groupName, size: 100 }
            });

            // 응답 데이터 처리
            if (response.data && response.data.data && response.data.data.content) {
                const existingRole = response.data.data.content.find(
                    role => role.name.toLowerCase() === groupName.toLowerCase()
                );

                return {
                    success: true,
                    data: {
                        isDuplicate: !!existingRole,
                        available: !existingRole
                    },
                    message: existingRole ? "이미 사용 중인 그룹명입니다." : "사용 가능한 그룹명입니다."
                };
            }

            // 기본 응답
            return {
                success: true,
                data: { isDuplicate: false, available: true },
                message: "사용 가능한 그룹명입니다."
            };
        } catch (error) {
            console.error("역할 그룹명 중복 확인 실패:", error);
            throw error;
        }
    },
    
    // 사용 가능한 모든 권한 목록 가져오기
    getAllPermissions: async () => {
        try {
            const response = await apiClient.get("/infra/permissions");
            return response.data;
        } catch (error) {
            console.error("권한 목록 가져오기 실패:", error);
            throw error;
        }
    }
};

export default roleApi;